import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Users, ChevronRight, Plus } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { api, authApi } from '@watchparty/shared';
// ---------------------------------------------------------------------------
// PartyCard
// ---------------------------------------------------------------------------
function PartyCard({ party }) {
    return (_jsx(Link, { to: "/parties/$partyId", params: { partyId: party.partyId }, style: { textDecoration: 'none' }, children: _jsxs("div", { style: {
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer',
                transition: 'border-color 200ms, background-color 200ms, box-shadow 200ms',
            }, onMouseEnter: e => e.currentTarget.style.borderColor = 'var(--color-border-strong)', onMouseLeave: e => e.currentTarget.style.borderColor = 'var(--color-border)', children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '14px' }, children: [_jsx("div", { style: {
                                width: '44px', height: '44px', borderRadius: 'var(--radius-md)',
                                background: 'var(--color-surface-sunken)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }, children: _jsx(Users, { size: 20, style: { color: 'var(--color-text-muted)' } }) }), _jsxs("div", { children: [_jsx("p", { style: {
                                        margin: 0,
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.9375rem',
                                        fontWeight: 700,
                                        color: 'var(--color-text)',
                                    }, children: party.name }), _jsxs("p", { style: {
                                        margin: '2px 0 0',
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.8rem',
                                        color: 'var(--color-text-muted)',
                                    }, children: [party.memberCount, " ", party.memberCount === 1 ? 'member' : 'members', ' · ', party.workCount, " ", party.workCount === 1 ? 'title' : 'titles'] })] })] }), _jsx(ChevronRight, { size: 18, style: { color: 'var(--color-text-faint)', flexShrink: 0 } })] }) }));
}
// ---------------------------------------------------------------------------
// PartiesList
// ---------------------------------------------------------------------------
export function PartiesList() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: parties, isLoading } = useQuery({
        queryKey: ['parties'],
        queryFn: () => api.get('/parties'),
    });
    const { data: limits } = useQuery({
        queryKey: ['myLimits'],
        queryFn: () => authApi.getMyLimits(),
    });
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [partyName, setPartyName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const userId = user?.userId;
    const socialParties = parties?.filter(p => !p.isPersonal) ?? [];
    const ownedParties = socialParties.filter(p => p.ownerId === userId);
    const joinedParties = socialParties.filter(p => p.ownerId !== userId);
    const atPartyLimit = limits != null && ownedParties.length >= limits.maxParties;
    async function handleCreateParty() {
        const trimmed = partyName.trim();
        if (!trimmed)
            return;
        setCreateError(null);
        setIsCreating(true);
        try {
            const party = await api.post('/parties', { name: trimmed });
            refreshUser();
            queryClient.invalidateQueries({ queryKey: ['parties'] });
            queryClient.invalidateQueries({ queryKey: ['myLimits'] });
            setShowCreateForm(false);
            setPartyName('');
            navigate({ to: '/parties/$partyId', params: { partyId: party.partyId } });
        }
        catch (err) {
            setCreateError(err instanceof Error ? err.message : 'Something went wrong.');
        }
        finally {
            setIsCreating(false);
        }
    }
    if (isLoading) {
        return (_jsxs("div", { style: { display: 'flex', justifyContent: 'center', padding: '64px 0' }, children: [_jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` }), _jsx("div", { style: {
                        width: '24px', height: '24px',
                        border: '2px solid var(--color-border)',
                        borderTopColor: 'var(--color-text)',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                    } })] }));
    }
    return (_jsxs("div", { children: [_jsxs("section", { style: { marginBottom: '40px' }, children: [_jsxs("div", { style: {
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: '12px',
                        }, children: [_jsxs("h2", { style: {
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'var(--color-text-muted)',
                                    margin: 0,
                                }, children: ["My ", ownedParties.length === 1 ? 'Party' : 'Parties'] }), atPartyLimit ? (_jsxs("button", { disabled: true, title: "Coming soon \u2014 additional parties will be available soon.", style: {
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    padding: '4px 10px',
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    background: 'transparent',
                                    color: 'var(--color-text-faint)',
                                    cursor: 'not-allowed',
                                    opacity: 0.5,
                                }, children: [_jsx(Plus, { size: 14 }), "Create Party"] })) : (_jsxs("button", { onClick: () => {
                                    setPartyName(user?.displayName ? `${user.displayName}'s Party` : 'My Party');
                                    setShowCreateForm(true);
                                }, style: {
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    padding: '4px 10px',
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    background: 'transparent',
                                    color: 'var(--color-text-muted)',
                                    cursor: 'pointer',
                                    transition: 'border-color 200ms, color 200ms',
                                }, onMouseEnter: e => {
                                    e.currentTarget.style.borderColor = 'var(--color-text-muted)';
                                    e.currentTarget.style.color = 'var(--color-text)';
                                }, onMouseLeave: e => {
                                    e.currentTarget.style.borderColor = 'var(--color-border)';
                                    e.currentTarget.style.color = 'var(--color-text-muted)';
                                }, children: [_jsx(Plus, { size: 14 }), "Create Party"] }))] }), showCreateForm && (_jsx("div", { style: {
                            padding: '20px',
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            marginBottom: '12px',
                        }, children: _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px' }, children: [_jsxs("div", { children: [_jsx("label", { style: {
                                                display: 'block',
                                                fontFamily: 'var(--font-family-body)',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: 'var(--color-text-muted)',
                                                marginBottom: '6px',
                                            }, children: "Party Name" }), _jsx("input", { type: "text", value: partyName, onChange: (e) => setPartyName(e.target.value), placeholder: "My Watch Party", maxLength: 100, autoFocus: true, onKeyDown: (e) => { if (e.key === 'Enter')
                                                handleCreateParty(); }, style: {
                                                display: 'flex', height: '40px', width: '100%',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border)',
                                                background: 'var(--color-surface)',
                                                padding: '0 12px',
                                                fontSize: '0.875rem', fontFamily: 'var(--font-family-body)',
                                                color: 'var(--color-text)',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                            } })] }), createError && (_jsx("p", { style: {
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.8125rem',
                                        color: 'var(--color-danger, #f87171)',
                                        margin: 0,
                                    }, children: createError })), _jsxs("div", { style: { display: 'flex', gap: '8px', justifyContent: 'flex-end' }, children: [_jsx("button", { onClick: () => { setShowCreateForm(false); setPartyName(''); setCreateError(null); }, style: {
                                                padding: '8px 14px',
                                                fontFamily: 'var(--font-family-body)',
                                                fontSize: '0.875rem', fontWeight: 600,
                                                border: 'none',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'transparent',
                                                color: 'var(--color-text-muted)',
                                                cursor: 'pointer',
                                            }, children: "Cancel" }), _jsx("button", { onClick: handleCreateParty, disabled: isCreating || !partyName.trim(), style: {
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                padding: '8px 16px',
                                                fontFamily: 'var(--font-family-body)',
                                                fontSize: '0.875rem', fontWeight: 700,
                                                border: 'none',
                                                borderRadius: 'var(--radius-md)',
                                                background: 'var(--color-text)',
                                                color: 'var(--color-bg)',
                                                cursor: isCreating || !partyName.trim() ? 'not-allowed' : 'pointer',
                                                opacity: isCreating || !partyName.trim() ? 0.5 : 1,
                                            }, children: isCreating ? 'Creating...' : 'Create' })] })] }) })), ownedParties.length > 0 ? (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px' }, children: ownedParties.map(party => (_jsx(PartyCard, { party: party }, party.partyId))) })) : (_jsx("div", { style: {
                            padding: '24px 20px',
                            background: 'var(--color-surface)',
                            border: '1px dashed var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            textAlign: 'center',
                        }, children: _jsx("p", { style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.875rem',
                                color: 'var(--color-text-muted)',
                                margin: 0,
                            }, children: "You haven't created a party yet." }) }))] }), _jsxs("section", { children: [_jsx("h2", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--color-text-muted)',
                            marginBottom: '12px',
                        }, children: "Member Of" }), joinedParties.length > 0 ? (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px' }, children: joinedParties.map(party => (_jsx(PartyCard, { party: party }, party.partyId))) })) : (_jsx("div", { style: {
                            padding: '24px 20px',
                            background: 'var(--color-surface)',
                            border: '1px dashed var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            textAlign: 'center',
                        }, children: _jsx("p", { style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.875rem',
                                color: 'var(--color-text-muted)',
                                margin: 0,
                            }, children: "You haven't joined any parties yet." }) }))] })] }));
}
